<template>
<div>
    <CCol sm="12">
        <CCard>
            <CCardBody class="d-flex flex-column">
                <div class="d-flex mb-2 justify-content-end align-items-start">
                    <p class="flex-grow-1">{{ $t('banner') }}</p>
                    <LanguageDropdown v-model="currentLanguage"></LanguageDropdown>
                </div>
                <!-- Feature description -->
                <div class="alert alert-info col-12 p-2 mb-3">
                    <small>
                        <i class="cil-info"></i>
                        {{ $t('bannerFeatureDescription') }}
                    </small>
                </div>
                <!-- Image -->
                <div role="group" class="form-group">
                    <label>{{ $t('imageMandatory') }}</label>
                    <div class="banner-image-picker">
                        <GalleryPicker class="gallery-picker" v-model="banner.image" :ratioWidth="1" :ratioHeight="1" v-bind:class="{ 'is-invalid': genericValidator(banner.image, attemptedSubmit) === false || errorValidator('Image') === false }" :max="1"></GalleryPicker>
                    </div>
                </div>
                <!-- Link -->
                <div role="group" class="form-group">
                    <label>{{ $t('linkOptional') }}</label>
                    <CInput type="text" v-model="banner.link" :placeholder="$t('linkPlaceholder')"></CInput>
                </div>
                <!-- Active -->
                <div class="form-group">
                    <div class="custom-control custom-checkbox">
                        <input
                            type="checkbox"
                            class="custom-control-input"
                            id="activeCheckbox"
                            v-model="banner.active"
                        />
                        <label class="custom-control-label" for="activeCheckbox">
                            {{ $t('active') }}
                        </label>
                    </div>
                </div>
                <!-- Position -->
                <div role="group" class="form-group">
                    <label>{{ $t('position') }}</label>
                    <CInput type="number" v-model.number="banner.position" :is-valid="genericValidator(banner.position !== undefined && banner.position !== null && banner.position !== '', attemptedSubmit)"></CInput>
                </div>
                <!-- Alt Text (per language) -->
                <div role="group" class="form-group">
                    <label>{{ $t('altTextMandatory') }}</label>
                    <CInput type="text" v-model="currentAltText" :is-valid="genericValidator(currentAltText, attemptedSubmit) && errorValidator('AltTexts')"></CInput>
                    <small class="text-muted d-block mt-1">{{ $t('altTextHelper') }}</small>
                </div>
                <!-- Validation alert -->
                <div v-if="attemptedSubmit && !isValid()" class="alert alert-danger col-12 p-2 mb-3">
                    <small>
                        <i class="cil-warning"></i>
                        {{ $t('pleaseFillAllLanguageFields') }}
                    </small>
                </div>
                <!-- Action button -->
                <CSpinner v-if="submitting" variant="success" small class="align-self-end mt-3"></CSpinner>
                <CButton v-else type="button" class="btn btn-primary align-self-end mt-3" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
                <FormLoading v-if="loading"></FormLoading>
            </CCardBody>
        </CCard>
    </CCol>
    <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup" v-model="showConfirmationPopup"></ConfirmationPopUp>
</div>
</template>

<script>
import { genericValidator, errorValidator, deepClone } from "@/helpers/utils.js";
import Banner from "@/classes/Banner";
import { mapState } from 'vuex'
import LanguageDropdown from '@/controls/LanguageDropdown.vue'
import { CONSTANTS } from '@/helpers/consts';
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import GalleryPicker from '@/controls/GalleryPicker.vue'
import FormLoading from '@/controls/FormLoading.vue'
import router from '@/router/index';

export default {
    components: { LanguageDropdown, ConfirmationPopUp, GalleryPicker, FormLoading },
    props:{
        baseBanner:
        {
            type: Object,
            default: null,
        },
        loading:
        {
            type: Boolean,
            default: false,
        },
    },
    data()
    {
        return {
            attemptedSubmit: false,
            currentLanguage: CONSTANTS.supportedLanguages[0] || { key: 'en', label: 'English' },
            banner: new Banner(),
            submitting: false,
            showConfirmationPopup: false,
            confirmationMessage: "",
        }
    },
    mounted: function()
    {
        this.resetBanner();
    },
    computed:
    {
        ...mapState({
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        ...mapState('banners', ['postBannerOutput']),
        ...mapState('banners', ['putBannerOutput']),
        errorOutput: function()
        {
            var result = {};

            if(this.alertType == 'alert-danger' && this.alertOutput && Array.isArray(this.alertOutput))
            {
                result = this.alertOutput.reduce(function(accum, currentVal) {
                    accum[currentVal.key] = currentVal.value;
                    return accum;
                }, {});
            }

            return result;
        },
        isEditMode: function()
        {
            return this.baseBanner && this.baseBanner.id;
        },
        currentAltText: {
            get() {
                if (!this.banner || !this.banner.altTexts) return '';
                const langKey = this.getLanguageKey();
                return this.banner.altTexts[langKey] || '';
            },
            set(value) {
                if (!this.banner) return;
                if (!this.banner.altTexts) {
                    this.banner.altTexts = { 1: '', 2: '', 3: '' };
                }
                const langKey = this.getLanguageKey();
                this.banner.altTexts[langKey] = value;
            }
        }
    },
    methods:{
        getLanguageKey() {
            const langMap = {
                'pt': 1,
                'en': 2,
                'es': 3
            };
            return langMap[this.currentLanguage.key] || 2;
        },
        resetBanner(){
            if(!this.baseBanner)
            {
                this.banner = new Banner();
            }
            else
            {
                this.banner = deepClone(new Banner(), this.baseBanner);
            }
        },
        submitClicked(){
            this.attemptedSubmit = true;
            if(this.isValid())
            {
                this.submitting = true;
                this.$emit('submit-requested', this.getSubmitData());
            }
        },
        isValid() {
            if (!this.banner.image) return false;
            if (this.banner.altTexts) {
                return Object.values(this.banner.altTexts).every(value =>
                    value && value.trim() !== ''
                );
            }
            return false;
        },
        getSubmitData() {
            const data = {
                link: this.banner.link || null,
                active: this.banner.active,
                position: this.banner.position,
                altTexts: {
                    pt: this.banner.altTexts[1] || '',
                    en: this.banner.altTexts[2] || '',
                    es: this.banner.altTexts[3] || ''
                }
            };

            // Include image if it has base64 data (new upload)
            if (this.banner.image && this.banner.image.data) {
                data.image = {
                    base64: this.banner.image.data,
                    name: this.banner.image.name || 'banner.png',
                    mime: this.banner.image.mime || 'image/png'
                };
            }

            if (this.banner.id) {
                data.id = this.banner.id;
            }

            return data;
        },
        genericValidator,
        errorValidator,
        onClickConfirmationPopup(value){
            this.showConfirmationPopup = value;
            if(!value)
            {
                router.push({name: "BannersList"});
            }
        },
    },
    watch: {
        baseBanner: function() {
            this.resetBanner();
        },
        postBannerOutput: function() {
            if (this.postBannerOutput === true) {
                this.showConfirmationPopup = true;
                this.submitting = false;
            }
        },
        putBannerOutput: function() {
            if (this.putBannerOutput === true) {
                this.showConfirmationPopup = true;
                this.submitting = false;
            }
        }
    }
}
</script>

<style scoped>
.banner-image-picker {
    max-width: 360px;
}
</style>
